import { useEffect, useMemo, useRef, useState } from "react";
import {
  ALL_USERS,
  C,
  CUST_PRIOS,
  TASK_CATS,
  TASK_CAT_STYLE,
  TASK_STATUSES,
  TODAY,
  inferTaskCat,
  parseMentions,
} from "../../../../constants";
import { daysUntil, getInit, relTime, uid, withUrgency } from "../../../../utils";
import { mapScoping } from "../../../../mappers";
import apiClient from "../../../../services/api";
import { DialogPrimitives } from "../../../../shared/ui";

const MT_TABS = [
  { id: "all", label: "All" },
  { id: "today", label: "Due Today" },
  { id: "overdue", label: "Overdue" },
  { id: "highprio", label: "High Priority" },
  { id: "custprio", label: "Priority Customers" },
  { id: "blocked", label: "Blocked" },
  { id: "completed", label: "Completed" },
];

export default function MyTasksPage({
  tasks,
  projects,
  setTasks,
  nav,
  addNotification,
  scoping,
  setScoping,
  audits,
  setAudits,
  leads,
  setLeads,
  currentUser,
  AvatarComponent,
  StatusChipComponent,
  PriorityChipComponent,
  DepartmentBadgeComponent,
  CustomerPriorityTagComponent,
  MentionInputComponent,
  RenderMentionsComponent,
}) {
  const [tab, setTab] = useState("all");
  const [sortBy, setSortBy] = useState("urgency");
  const [groupBy, setGroupBy] = useState("none");
  const [taskCat, setTaskCat] = useState("All");
  const [viewMode, setViewMode] = useState("list");
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [detailTask, setDetailTask] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);

  const pMap = useMemo(() => Object.fromEntries(projects.map((project) => [project.projectId, project])), [projects]);
  const tasksU = useMemo(() => withUrgency(tasks, projects), [tasks, projects]);
  const mine = useMemo(
    () => tasksU.filter((task) => task.assignee === currentUser.name || (task.contributors || []).includes(currentUser.name)),
    [currentUser.name, tasksU],
  );

  const normalizedScopingTasks = useMemo(() => {
    const out = [];
    (scoping || []).forEach((card) => {
      (card.tasks || []).forEach((task) => {
        const isAssigned = (task.assigneeId && task.assigneeId === currentUser.id)
          || (task.assignee && (task.assignee === currentUser.name || task.assignee === currentUser.email));
        if (!isAssigned) return;
        const stageColor = { "1. New": "#60a5fa", "2. Customer Info": "#a78bfa", "3. Estimation": "#ec4899", "4. Proposal": "#f97316", "5. Developer Matching": "#f59e0b", "6. Meeting": "#14b8a6", "7. Done": "#10c981", "8. Lost": "#6b7280" }[card.stage] || "#60a5fa";
        out.push({
          taskId: `scoping_${card.id}_${task.id}`,
          title: task.title,
          status: task.done ? "Done" : "In Progress",
          priority: card.priority || "Medium",
          dueDate: task.dueDate || null,
          assignee: task.assignee || currentUser.name,
          assigneeId: task.assigneeId || null,
          department: "Sales",
          taskCategory: "Scoping",
          urgencyScore: task.dueDate ? Math.max(0, Math.min(100, 100 - daysUntil(task.dueDate) * 3)) : 20,
          projectId: card.id,
          projectName: card.name,
          clientName: card.stage?.replace(/^\d\.\s/, "") || "Scoping",
          contributors: [],
          notes: task.description || null,
          starred: false,
          customerPriority: "Standard",
          source: "scoping",
          _stageColor: stageColor,
          _cardId: card.id,
          _taskId: task.id,
          _subtaskId: null,
          _type: "task",
          _done: task.done,
        });
      });
      (card.tasks || []).forEach((task) => {
        (task.subtasks || []).forEach((subtask) => {
          const isAssigned = (subtask.assigneeId && subtask.assigneeId === currentUser.id)
            || (subtask.assignee && (subtask.assignee === currentUser.name || subtask.assignee === currentUser.email));
          if (!isAssigned) return;
          const stageColor = { "1. New": "#60a5fa", "2. Customer Info": "#a78bfa", "3. Estimation": "#ec4899", "4. Proposal": "#f97316", "5. Developer Matching": "#f59e0b", "6. Meeting": "#14b8a6", "7. Done": "#10c981", "8. Lost": "#6b7280" }[card.stage] || "#60a5fa";
          out.push({
            taskId: `scoping_${card.id}_${task.id}_${subtask.id}`,
            title: subtask.title,
            status: subtask.done ? "Done" : "In Progress",
            priority: card.priority || "Medium",
            dueDate: subtask.dueDate || null,
            assignee: subtask.assignee || currentUser.name,
            assigneeId: subtask.assigneeId || null,
            department: "Sales",
            taskCategory: "Scoping",
            urgencyScore: subtask.dueDate ? Math.max(0, Math.min(100, 100 - daysUntil(subtask.dueDate) * 3)) : 20,
            projectId: card.id,
            projectName: card.name,
            clientName: card.stage?.replace(/^\d\.\s/, "") || "Scoping",
            contributors: [],
            notes: null,
            starred: false,
            customerPriority: "Standard",
            source: "scoping",
            _stageColor: stageColor,
            _cardId: card.id,
            _taskId: task.id,
            _subtaskId: subtask.id,
            _type: "subtask",
            _done: subtask.done,
            _parentTitle: task.title,
          });
        });
      });
    });
    return out;
  }, [currentUser.email, currentUser.id, currentUser.name, scoping]);

  const normalizedAuditTasks = useMemo(() => {
    const out = [];
    (audits || []).forEach((audit) => {
      (audit.auditTasks || []).forEach((task) => {
        const isAssigned = (task.assigneeId && task.assigneeId === currentUser.id) || (task.assignee && task.assignee === currentUser.name);
        if (!isAssigned) return;
        out.push({
          taskId: `audit_${audit.id}_${task.id}`,
          title: task.title,
          status: task.status || "To Do",
          priority: "Medium",
          dueDate: task.dueDate || null,
          assignee: task.assignee || currentUser.name,
          assigneeId: task.assigneeId || null,
          department: "Delivery",
          taskCategory: "Audit",
          urgencyScore: task.dueDate ? Math.max(0, Math.min(100, 100 - daysUntil(task.dueDate) * 3)) : 20,
          projectId: audit.id,
          projectName: audit.name,
          clientName: audit.type || "Audit",
          contributors: [],
          notes: task.description || null,
          starred: false,
          customerPriority: "Standard",
          source: "audit",
          _auditId: audit.id,
          _taskId: task.id,
          _done: task.status === "Done",
        });
      });
    });
    return out;
  }, [audits, currentUser.id, currentUser.name]);

  const normalizedLeadTasks = useMemo(() => {
    const out = [];
    (leads || []).forEach((lead) => {
      (lead.tasks || []).forEach((task) => {
        const isAssigned = (task.assigneeId && task.assigneeId === currentUser.id) || (task.assignee && task.assignee === currentUser.name);
        if (!isAssigned) return;
        out.push({
          taskId: `lead_${lead.id}_${task.id}`,
          title: task.title,
          status: task.done ? "Done" : "In Progress",
          priority: "Medium",
          dueDate: task.dueDate || null,
          assignee: task.assignee || currentUser.name,
          assigneeId: task.assigneeId || null,
          department: "Sales",
          taskCategory: "Sales",
          urgencyScore: task.dueDate ? Math.max(0, Math.min(100, 100 - daysUntil(task.dueDate) * 3)) : 20,
          projectId: lead.id,
          projectName: lead.name,
          clientName: lead.status || "Lead",
          contributors: [],
          notes: null,
          starred: false,
          customerPriority: "Standard",
          source: "lead",
          _leadId: lead.id,
          _taskId: task.id,
          _subtaskId: null,
          _type: "task",
          _done: task.done,
        });
        (task.subtasks || []).forEach((subtask) => {
          const isSubtaskAssigned = (subtask.assigneeId && subtask.assigneeId === currentUser.id) || (subtask.assignee && subtask.assignee === currentUser.name);
          if (!isSubtaskAssigned) return;
          out.push({
            taskId: `lead_${lead.id}_${task.id}_${subtask.id}`,
            title: subtask.title,
            status: subtask.done ? "Done" : "In Progress",
            priority: "Medium",
            dueDate: subtask.dueDate || null,
            assignee: subtask.assignee || currentUser.name,
            assigneeId: subtask.assigneeId || null,
            department: "Sales",
            taskCategory: "Sales",
            urgencyScore: subtask.dueDate ? Math.max(0, Math.min(100, 100 - daysUntil(subtask.dueDate) * 3)) : 20,
            projectId: lead.id,
            projectName: lead.name,
            clientName: lead.status || "Lead",
            contributors: [],
            notes: null,
            starred: false,
            customerPriority: "Standard",
            source: "lead",
            _leadId: lead.id,
            _taskId: task.id,
            _subtaskId: subtask.id,
            _type: "subtask",
            _done: subtask.done,
            _parentTitle: task.title,
          });
        });
      });
    });
    return out;
  }, [currentUser.id, currentUser.name, leads]);

  const allMine = useMemo(() => [...mine, ...normalizedScopingTasks, ...normalizedAuditTasks, ...normalizedLeadTasks], [mine, normalizedAuditTasks, normalizedLeadTasks, normalizedScopingTasks]);
  const mineCat = useMemo(() => (taskCat === "All" ? allMine : allMine.filter((task) => (task.taskCategory || inferTaskCat(task.department)) === taskCat)), [allMine, taskCat]);
  const calTasks = useMemo(() => mineCat.filter((task) => task.status !== "Cancelled"), [mineCat]);

  const counts = {
    all: mineCat.filter((task) => task.status !== "Cancelled").length,
    today: mineCat.filter((task) => daysUntil(task.dueDate) === 0 && task.status !== "Done" && task.status !== "Cancelled").length,
    overdue: mineCat.filter((task) => daysUntil(task.dueDate) < 0 && task.status !== "Done" && task.status !== "Cancelled").length,
    highprio: mineCat.filter((task) => (task.priority === "Critical" || task.priority === "High") && task.status !== "Done").length,
    custprio: mineCat.filter((task) => (task.customerPriority === "VIP" || task.customerPriority === "Escalated" || task.customerPriority === "Strategic") && task.status !== "Done").length,
    blocked: mineCat.filter((task) => task.status === "Blocked" || task.status === "Waiting on Internal").length,
    completed: mineCat.filter((task) => task.status === "Done").length,
  };

  function getTab(taskList) {
    switch (tab) {
      case "today":
        return taskList.filter((task) => daysUntil(task.dueDate) === 0 && task.status !== "Done" && task.status !== "Cancelled");
      case "overdue":
        return taskList.filter((task) => daysUntil(task.dueDate) < 0 && task.status !== "Done" && task.status !== "Cancelled");
      case "highprio":
        return taskList.filter((task) => (task.priority === "Critical" || task.priority === "High") && task.status !== "Done");
      case "custprio":
        return taskList.filter((task) => (task.customerPriority === "VIP" || task.customerPriority === "Escalated" || task.customerPriority === "Strategic") && task.status !== "Done");
      case "blocked":
        return taskList.filter((task) => task.status === "Blocked" || task.status === "Waiting on Internal");
      case "completed":
        return taskList.filter((task) => task.status === "Done");
      default:
        return taskList.filter((task) => task.status !== "Cancelled");
    }
  }

  const filtered = getTab(mineCat).sort((left, right) => {
    if (sortBy === "urgency") return right.urgencyScore - left.urgencyScore;
    if (sortBy === "dueDate") return new Date(left.dueDate) - new Date(right.dueDate);
    if (sortBy === "custPriority") return CUST_PRIOS.indexOf(right.customerPriority) - CUST_PRIOS.indexOf(left.customerPriority);
    if (sortBy === "project") return (pMap[left.projectId]?.projectName || left.projectName || "").localeCompare(pMap[right.projectId]?.projectName || right.projectName || "");
    return 0;
  });

  function groups() {
    if (groupBy === "none") return [{ key: "", label: "", items: filtered }];
    const map = {};
    filtered.forEach((task) => {
      let key;
      if (groupBy === "project") key = pMap[task.projectId]?.projectName || task.projectName || "Unknown";
      else if (groupBy === "client") key = pMap[task.projectId]?.clientName || task.clientName || "Unknown";
      else if (groupBy === "department") key = task.department;
      else key = task.status;
      if (!map[key]) map[key] = [];
      map[key].push(task);
    });
    return Object.entries(map).map(([key, value]) => ({ key, label: key, items: value }));
  }

  const markDone = (id) => setTasks((state) => state.map((task) => (task.taskId === id ? { ...task, status: "Done", completedDate: "2026-03-24", changelog: [...(task.changelog || []), { id: uid(), ts: "2026-03-24", author: currentUser.name, action: "changed", field: "status", from: task.status, to: "Done" }] } : task)));
  const toggleStar = (id) => setTasks((state) => state.map((task) => (task.taskId === id ? { ...task, starred: !task.starred } : task)));
  const updateTask = (updated) => setTasks((state) => state.map((task) => (task.taskId === updated.taskId ? updated : task)));

  const prevAssignRef = useRef(null);
  useEffect(() => {
    const nextMap = new Map(tasks.map((task) => [task.taskId, task.assignee]));
    const prev = prevAssignRef.current;
    prevAssignRef.current = nextMap;
    if (!prev || !addNotification) return;
    tasks.forEach((task) => {
      const wasAssigned = prev.get(task.taskId) === currentUser.name;
      if (!wasAssigned && task.assignee === currentUser.name && task.status !== "Done" && task.status !== "Cancelled") {
        addNotification({ id: uid(), type: "assignment", text: `You've been assigned: "${task.title}"`, ts: new Date().toISOString(), read: false });
      }
    });
  }, [addNotification, currentUser.name, tasks]);

  function markScopingTaskDone(entry) {
    const currentDone = entry._done ?? entry.done ?? false;
    const newDone = !currentDone;
    if (setScoping) {
      setScoping((state) => state.map((card) => {
        if (card.id !== entry._cardId) return card;
        return {
          ...card,
          tasks: (card.tasks || []).map((task) => {
            if (task.id !== entry._taskId) return task;
            if (entry._type === "task") return { ...task, done: newDone };
            return {
              ...task,
              subtasks: (task.subtasks || []).map((subtask) => (subtask.id === entry._subtaskId ? { ...subtask, done: newDone } : subtask)),
            };
          }),
        };
      }));
    }
    const apiCall = entry._type === "task" ? apiClient.toggleScopingTaskDone(entry._taskId, newDone) : apiClient.toggleScopingSubtaskDone(entry._subtaskId, newDone);
    apiCall.then((updated) => {
      if (setScoping && updated) setScoping((state) => state.map((card) => (card.id === entry._cardId ? mapScoping(updated) : card)));
    }).catch(() => {
      if (setScoping) {
        setScoping((state) => state.map((card) => {
          if (card.id !== entry._cardId) return card;
          return {
            ...card,
            tasks: (card.tasks || []).map((task) => {
              if (task.id !== entry._taskId) return task;
              if (entry._type === "task") return { ...task, done: currentDone };
              return {
                ...task,
                subtasks: (task.subtasks || []).map((subtask) => (subtask.id === entry._subtaskId ? { ...subtask, done: currentDone } : subtask)),
              };
            }),
          };
        }));
      }
    });
  }

  function markAuditTaskDone(entry) {
    const currentDone = entry._done ?? false;
    const newStatus = currentDone ? "In Progress" : "Done";
    if (setAudits) {
      setAudits((state) => state.map((audit) => (audit.id !== entry._auditId ? audit : { ...audit, auditTasks: (audit.auditTasks || []).map((task) => (task.id === entry._taskId ? { ...task, status: newStatus } : task)) })));
    }
    apiClient.updateAuditTask(entry._auditId, entry._taskId, { status: newStatus }).catch(() => {
      if (setAudits) {
        setAudits((state) => state.map((audit) => (audit.id !== entry._auditId ? audit : { ...audit, auditTasks: (audit.auditTasks || []).map((task) => (task.id === entry._taskId ? { ...task, status: currentDone ? "Done" : "In Progress" } : task)) })));
      }
    });
  }

  function markLeadTaskDone(entry) {
    const currentDone = entry._done ?? false;
    const nextDone = !currentDone;

    if (setLeads) {
      setLeads((state) => state.map((lead) => {
        if (lead.id !== entry._leadId) return lead;
        return {
          ...lead,
          tasks: (lead.tasks || []).map((task) => {
            if (task.id !== entry._taskId) return task;
            if (entry._type === "task") {
              return { ...task, done: nextDone, doneAt: nextDone ? new Date().toISOString() : null };
            }
            return {
              ...task,
              subtasks: (task.subtasks || []).map((subtask) => (subtask.id === entry._subtaskId ? { ...subtask, done: nextDone, doneAt: nextDone ? new Date().toISOString() : null } : subtask)),
            };
          }),
        };
      }));
    }

    const apiCall = entry._type === "task"
      ? apiClient.updateLeadTask(entry._leadId, entry._taskId, { done: nextDone })
      : apiClient.updateLeadSubtask(entry._leadId, entry._taskId, entry._subtaskId, { done: nextDone });

    apiCall.then((updatedLead) => {
      if (setLeads && updatedLead) {
        setLeads((state) => state.map((lead) => (lead.id === entry._leadId ? updatedLead : lead)));
      }
    }).catch(() => {
      if (setLeads) {
        setLeads((state) => state.map((lead) => {
          if (lead.id !== entry._leadId) return lead;
          return {
            ...lead,
            tasks: (lead.tasks || []).map((task) => {
              if (task.id !== entry._taskId) return task;
              if (entry._type === "task") {
                return { ...task, done: currentDone, doneAt: currentDone ? task.doneAt : null };
              }
              return {
                ...task,
                subtasks: (task.subtasks || []).map((subtask) => (subtask.id === entry._subtaskId ? { ...subtask, done: currentDone, doneAt: currentDone ? subtask.doneAt : null } : subtask)),
              };
            }),
          };
        }));
      }
    });
  }

  function CalendarView() {
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = Array.from({ length: daysInMonth }, (_, index) => index + 1);
    const todayDay = TODAY.getMonth() === month && TODAY.getFullYear() === year ? TODAY.getDate() : null;
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const byClient = {};

    calTasks.forEach((task) => {
      const client = pMap[task.projectId]?.clientName || task.clientName || "Other";
      if (!byClient[client]) byClient[client] = [];
      byClient[client].push(task);
    });

    const clientEntries = Object.entries(byClient).sort((left, right) => right[1].length - left[1].length);
    if (!clientEntries.length) return <div className="tl-empty-msg">No tasks to display for this month. Try adjusting your filters or switching months.</div>;

    const CLIENT_W = 170;
    const COL_W = 38;
    const gridTemplate = `${CLIENT_W}px ${daysInMonth * COL_W}px`;

    function getBarStyle(task) {
      if (!task.dueDate) return null;
      const startDate = new Date(task.startDate || task.dueDate);
      const endDate = new Date(task.dueDate);
      const startMonth = startDate.getMonth();
      const startYear = startDate.getFullYear();
      const endMonth = endDate.getMonth();
      const endYear = endDate.getFullYear();
      let startDay = startMonth === month && startYear === year ? startDate.getDate() : 1;
      let endDay = endMonth === month && endYear === year ? endDate.getDate() : daysInMonth;
      if (endDay < startDay) endDay = startDay;
      if (endDay < 1 || startDay > daysInMonth) return null;
      const left = (startDay - 1) * COL_W + 3;
      const width = Math.max(COL_W - 6, (endDay - startDay + 1) * COL_W - 6);
      const isOD = daysUntil(task.dueDate) < 0 && task.status !== "Done";
      const isDone = task.status === "Done";
      const bg = isDone ? "#10c981" : isOD ? "#ef4444" : task.priority === "Critical" ? "#ef4444" : task.priority === "High" ? "#f59e0b" : "#3b82f6";
      return { left, width, bg, isDone };
    }

    function fmtDate(date) {
      return new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    }

    return (
      <div>
        <div className="cal-nav-row">
          <button className="cal-nav-btn" onClick={() => setCalMonth((value) => { const next = new Date(value); next.setMonth(next.getMonth() - 1); return next; })}>← Prev</button>
          <span className="cal-month-title">{calMonth.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</span>
          <button className="cal-nav-btn" onClick={() => setCalMonth((value) => { const next = new Date(value); next.setMonth(next.getMonth() + 1); return next; })}>Next →</button>
        </div>
        <div className="tl-container">
          <div className="tl-scroll">
            <div className="tl-header" style={{ display: "grid", gridTemplateColumns: gridTemplate }}>
              <div className="tl-header-client">Client</div>
              <div className="tl-header-days" style={{ display: "grid", gridTemplateColumns: `repeat(${daysInMonth},${COL_W}px)` }}>
                {days.map((day) => {
                  const dayOfWeek = new Date(year, month, day).getDay();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  return <div key={day} className={`tl-day-hdr${day === todayDay ? " today" : ""}${isWeekend ? " weekend" : ""}`}>{day}<span className="tl-day-name">{dayNames[dayOfWeek]}</span></div>;
                })}
              </div>
            </div>
            {clientEntries.map(([clientName, clientTasks], index) => (
              <div key={clientName} className="tl-client-group" style={{ background: index % 2 === 0 ? "rgba(10,21,40,.2)" : "transparent" }}>
                <div className="tl-client-row" style={{ display: "grid", gridTemplateColumns: gridTemplate }}>
                  <div className="tl-client-label">
                    <span className="tl-client-name" title={clientName}>{clientName}</span>
                    <span className="tl-client-count">{clientTasks.length} task{clientTasks.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="tl-task-area" style={{ position: "relative", minHeight: clientTasks.length * 36 + 6 }}>
                    {days.map((day) => {
                      const dayOfWeek = new Date(year, month, day).getDay();
                      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                      return <div key={day} className={`tl-grid-col${day === todayDay ? " today" : ""}${isWeekend ? " weekend" : ""}`} style={{ left: (day - 1) * COL_W, width: COL_W }} />;
                    })}
                    {clientTasks.map((task) => {
                      const barStyle = getBarStyle(task);
                      if (!barStyle) return null;
                      const isHovered = hoveredBar === task.taskId;
                      return (
                        <div key={task.taskId} className="tl-task-lane" style={{ height: 36 }}>
                          <div className={`tl-bar${barStyle.isDone ? " done" : ""}`} style={{ left: barStyle.left, width: barStyle.width, background: barStyle.bg, top: 6 }} onClick={() => setDetailTask(task)} onMouseEnter={() => setHoveredBar(task.taskId)} onMouseLeave={() => setHoveredBar(null)}>
                            <span className="tl-bar-dot" />
                            <span className="tl-bar-label">{task.title}</span>
                            {isHovered && (
                              <div className="tl-tooltip" onClick={(event) => event.stopPropagation()}>
                                <div className="tl-tooltip-title">{task.title}</div>
                                <div className="tl-tooltip-row"><span className="tl-tooltip-dim">Status</span><span>{task.status}</span></div>
                                <div className="tl-tooltip-row"><span className="tl-tooltip-dim">Priority</span><span>{task.priority}</span></div>
                                <div className="tl-tooltip-row"><span className="tl-tooltip-dim">Due</span><span>{fmtDate(task.dueDate)}</span></div>
                                <div className="tl-tooltip-row"><span className="tl-tooltip-dim">Assignee</span><span>{task.assignee}</span></div>
                                {task.notes && <div style={{ marginTop: 4, color: "#8fa4c8", borderTop: "1px solid #2a4070", paddingTop: 4, fontSize: 10 }}>{task.notes}</div>}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="tl-legend">
            {[["#3b82f6", "Normal"], ["#f59e0b", "High Priority"], ["#ef4444", "Overdue / Critical"], ["#10c981", "Completed"]].map(([color, label]) => <span key={label} className="tl-legend-item"><span className="tl-legend-dot" style={{ background: color }} />{label}</span>)}
            <span style={{ marginLeft: "auto", fontSize: 10 }}>Hover for details · Click to open</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade">
      {(() => {
        const activeCount = allMine.filter(t => t.status !== "Done" && t.status !== "Cancelled" && !t._done).length;
        return (
          <div className="pg-top">
            <div className="scoping-top-row">
              <div>
                <div className="pg-title">My Tasks</div>
                <div className="pg-sub">Personal work queue for {currentUser.name} · {activeCount} active · includes tasks where you are a contributor</div>
              </div>
              <div className="cal-toggle" style={{ marginTop: 4 }}>
                <button className={`cal-toggle-btn${viewMode === "list" ? " active" : ""}`} onClick={() => setViewMode("list")}>☰ List</button>
                <button className={`cal-toggle-btn${viewMode === "calendar" ? " active" : ""}`} onClick={() => setViewMode("calendar")}>▦ Calendar</button>
              </div>
            </div>
          </div>
        );
      })()}
      <div className="mt-shell">
        <div className="cat-filter-row">
          <span className="cat-filter-lbl">Category:</span>
          {["All", ...TASK_CATS].map((cat) => {
            const style = cat !== "All" ? TASK_CAT_STYLE[cat] : null;
            const count = cat === "All" ? counts.all : allMine.filter((task) => (task.taskCategory || inferTaskCat(task.department)) === cat && task.status !== "Cancelled").length;
            return (
              <button key={cat} className={`cat-btn${taskCat === cat ? " active" : ""}`} style={taskCat === cat && style ? { color: style.fg, borderColor: style.fg, background: style.bg } : taskCat === cat ? { color: C.blue, borderColor: C.blue, background: "rgba(59,127,250,.1)" } : {}} onClick={() => setTaskCat(cat)}>
                {style && <span style={{ width: 6, height: 6, borderRadius: "50%", background: style.fg, display: "inline-block" }} />}
                {cat} <span style={{ fontSize: 10, opacity: 0.7 }}>({count})</span>
              </button>
            );
          })}
        </div>
        {viewMode === "calendar" && <CalendarView />}
        {viewMode === "list" && (
          <>
            <div className="mt-tabs">
              {MT_TABS.map((item) => <div key={item.id} className={`mt-tab${tab === item.id ? " active" : ""}`} onClick={() => setTab(item.id)}>{item.label}<span className="mt-cnt">{counts[item.id] || 0}</span></div>)}
            </div>
            <div className="mt-ctrl">
              <span className="mt-ctrl-lbl">Sort:</span>
              <select className="fsel" value={sortBy} onChange={(event) => setSortBy(event.target.value)} style={{ fontSize: 12 }}>
                <option value="urgency">Urgency Score</option>
                <option value="dueDate">Due Date</option>
                <option value="project">Project</option>
                <option value="custPriority">Customer Priority</option>
              </select>
              <span className="mt-ctrl-lbl" style={{ marginLeft: 8 }}>Group:</span>
              <select className="fsel" value={groupBy} onChange={(event) => setGroupBy(event.target.value)} style={{ fontSize: 12 }}>
                <option value="none">None</option>
                <option value="project">By Project</option>
                <option value="client">By Client</option>
                <option value="department">By Department</option>
                <option value="status">By Status</option>
              </select>
              <div className="finfo"><strong>{viewMode === "calendar" ? calTasks.length : filtered.length}</strong> task{(viewMode === "calendar" ? calTasks.length : filtered.length) !== 1 ? "s" : ""}</div>
            </div>
            {groups().map((group) => (
              <div key={group.key}>
                {group.label && <div className="mt-gh"><span className="mt-gh-title">{group.label}</span><span style={{ fontSize: 10, color: C.dim, fontFamily: "'Noto Sans Mono',monospace" }}>{group.items.length}</span></div>}
                {group.items.map((task) => {
                  const isScoping = task.source === "scoping";
                  const isAudit = task.source === "audit";
                  const isLead = task.source === "lead";
                  const project = pMap[task.projectId];
                  const projectName = project?.projectName || task.projectName || task.projectId;
                  const clientName = project?.clientName || task.clientName || "";
                  const isOD = daysUntil(task.dueDate) < 0 && task.status !== "Done";
                  const isBlocked = task.status === "Blocked" || task.status === "Waiting on Internal";
                  const isDone = task.status === "Done";
                  const dueIn = daysUntil(task.dueDate);
                  const dueColor = isDone ? C.dim : isOD ? C.red : dueIn <= 3 ? C.yellow : C.dim;
                  const dueLabel = task.dueDate ? (isDone ? "Done" : isOD ? `${Math.abs(dueIn)}d overdue` : dueIn === 0 ? "Due today" : dueIn === 1 ? "Due tomorrow" : `Due in ${dueIn}d`) : (isDone ? "Done" : "No due date");
                  const contributors = [task.assignee, ...(task.contributors || [])].filter(Boolean);
                  const cat = task.taskCategory || inferTaskCat(task.department);
                  return (
                    <div key={task.taskId} className={`tc${isDone ? " dn" : isOD && !isScoping && !isAudit && !isLead ? " od" : isBlocked ? " bl" : task.starred ? " st" : ""}`} style={isScoping ? { borderLeftColor: `${task._stageColor}66` } : isAudit ? { borderLeftColor: "rgba(168,85,247,.5)" } : isLead ? { borderLeftColor: "rgba(16,185,129,.45)" } : {}}>
                      <div className={`tc-top${isDone ? " tc-dn" : ""}`}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="tc-title">{task.starred && "⭐ "}{(isScoping || isLead) && task._type === "subtask" && <span style={{ fontSize: 10, color: C.dim, marginRight: 4 }}>↳ {task._parentTitle} /</span>}{task.title}</div>
                          <div className="tc-meta"><strong style={{ color: C.sub }}>{projectName}</strong>{clientName && <span style={{ color: C.dim }}> · {clientName}</span>}</div>
                        </div>
                        <UrgencyScore score={task.urgencyScore} dueLabel={dueLabel} dueColor={dueColor} />
                      </div>
                      <div className="tc-chips">
                        <StatusChipComponent status={task.status} />
                        <PriorityChipComponent priority={task.priority} />
                        {!isScoping && !isAudit && <DepartmentBadgeComponent dept={task.department} />}
                        {!isScoping && !isAudit && <CustomerPriorityTagComponent cp={task.customerPriority} />}
                        <CategoryChip cat={cat} />
                        {isScoping && <span className="chip" style={{ color: "#f59e0b", background: "rgba(245,158,11,.1)", border: "1px solid rgba(245,158,11,.22)", fontSize: "9.5px" }}>◈ Scoping</span>}
                        {isAudit && <span className="chip" style={{ color: "#a855f7", background: "rgba(168,85,247,.1)", border: "1px solid rgba(168,85,247,.22)", fontSize: "9.5px" }}>◈ Audit</span>}
                        {isLead && <span className="chip" style={{ color: "#10b981", background: "rgba(16,185,129,.1)", border: "1px solid rgba(16,185,129,.22)", fontSize: "9.5px" }}>◈ Lead</span>}
                        {isBlocked && <span className="bwarn">⊘ {task.status}</span>}
                        {isOD && !isDone && <span className="bcrit">⚠ Overdue</span>}
                      </div>
                      {task.notes && <div className="tc-notes">{task.notes}</div>}
                      <div className="tc-acts">
                        {!isDone && (isScoping ? <button className="ta ta-done" onClick={() => markScopingTaskDone(task)}>✓ Mark Done</button> : isAudit ? <button className="ta ta-done" onClick={() => markAuditTaskDone(task)}>✓ Mark Done</button> : isLead ? <button className="ta ta-done" onClick={() => markLeadTaskDone(task)}>✓ Mark Done</button> : <button className="ta ta-done" onClick={() => markDone(task.taskId)}>✓ Mark Done</button>)}
                        {isDone && isScoping && <button className="ta" style={{ color: C.dim }} onClick={() => markScopingTaskDone(task)}>↺ Reopen</button>}
                        {isDone && isAudit && <button className="ta" style={{ color: C.dim }} onClick={() => markAuditTaskDone(task)}>↺ Reopen</button>}
                        {isDone && isLead && <button className="ta" style={{ color: C.dim }} onClick={() => markLeadTaskDone(task)}>↺ Reopen</button>}
                        {!isScoping && !isAudit && !isLead && <button className={`ta ta-star${task.starred ? " on" : ""}`} onClick={() => toggleStar(task.taskId)}>{task.starred ? "★ Starred" : "☆ Star"}</button>}
                        {!isScoping && !isAudit && !isLead && <button className="ta ta-collab" onClick={() => setDetailTask(task)}>💬 Collaborate</button>}
                        {!isScoping && !isAudit && !isLead && <button className="ta ta-open" onClick={() => nav("project", task.projectId)}>↗ Project</button>}
                        {isScoping && <button className="ta ta-open" onClick={() => nav("scoping")}>↗ Scoping</button>}
                        {isAudit && <button className="ta ta-open" onClick={() => nav("audits")}>↗ Audits</button>}
                        {isLead && <button className="ta ta-open" onClick={() => nav("leads")}>↗ Leads</button>}
                        {contributors.length > 1 && <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}><AvatarStack names={contributors} AvatarComponent={AvatarComponent} /><span style={{ fontSize: 10, color: C.dim }}>{contributors.length}</span></div>}
                        {(task.taskComments || []).length > 0 && <span style={{ fontSize: 10.5, color: C.dim, marginLeft: contributors.length > 1 ? 0 : "auto" }}>💬{(task.taskComments || []).length}</span>}
                      </div>
                    </div>
                  );
                })}
                {group.items.length === 0 && <div style={{ textAlign: "center", padding: "28px 0", color: C.dim, fontSize: 12 }}>No tasks in this category.</div>}
              </div>
            ))}
            {filtered.length === 0 && groups().length <= 1 && <div style={{ textAlign: "center", padding: "48px 0", color: C.dim, fontSize: 13 }}>All clear — no tasks in this filter.</div>}
          </>
        )}
      </div>
      {detailTask && <TaskDetailModal task={tasks.find((task) => task.taskId === detailTask.taskId) || detailTask} project={pMap[detailTask.projectId]} onClose={() => setDetailTask(null)} onUpdate={(task) => { updateTask(task); setDetailTask(task); }} addNotification={addNotification} currentUser={currentUser} AvatarComponent={AvatarComponent} StatusChipComponent={StatusChipComponent} PriorityChipComponent={PriorityChipComponent} DepartmentBadgeComponent={DepartmentBadgeComponent} CustomerPriorityTagComponent={CustomerPriorityTagComponent} MentionInputComponent={MentionInputComponent} RenderMentionsComponent={RenderMentionsComponent} />}
    </div>
  );
}

function TaskDetailModal({
  task,
  project,
  onClose,
  onUpdate,
  addNotification,
  currentUser,
  AvatarComponent,
  StatusChipComponent,
  PriorityChipComponent,
  DepartmentBadgeComponent,
  CustomerPriorityTagComponent,
  MentionInputComponent,
  RenderMentionsComponent,
}) {
  const [commentText, setCommentText] = useState("");
  const [linkInput, setLinkInput] = useState("");
  const [contribSel, setContribSel] = useState("");

  if (!task) return null;

  const allContribs = [task.assignee, ...(task.contributors || [])].filter(Boolean);
  const available = ALL_USERS.filter((user) => !allContribs.includes(user));

  function changeStatus(newStatus) {
    const changelog = { id: uid(), ts: "2026-03-24T09:00:00", author: currentUser.name, action: "changed", field: "status", from: task.status, to: newStatus };
    onUpdate({ ...task, status: newStatus, changelog: [...(task.changelog || []), changelog], updatedAt: "2026-03-24" });
  }

  function addContributor(name) {
    if (!name || allContribs.includes(name)) return;
    onUpdate({ ...task, contributors: [...(task.contributors || []), name], updatedAt: "2026-03-24" });
    setContribSel("");
  }

  function removeContributor(name) {
    if (name === task.assignee) return;
    onUpdate({ ...task, contributors: (task.contributors || []).filter((contributor) => contributor !== name), updatedAt: "2026-03-24" });
  }

  function postComment() {
    if (!commentText.trim()) return;
    const mentions = parseMentions(commentText);
    const comment = { id: uid(), author: currentUser.name, initials: currentUser.initials, text: commentText.trim(), ts: "2026-03-24T09:00:00", mentions };
    onUpdate({ ...task, taskComments: [...(task.taskComments || []), comment], updatedAt: "2026-03-24" });
    mentions.forEach((mention) => {
      if (mention.toLowerCase() === currentUser.name.toLowerCase()) {
        addNotification?.({ id: uid(), type: "mention", text: `You were @mentioned in task "${task.title}"`, ts: new Date().toISOString(), read: false });
      }
    });
    setCommentText("");
  }

  function addLink() {
    if (!linkInput.trim()) return;
    const attachment = { id: uid(), name: linkInput.replace(/^https?:\/\//, "").slice(0, 40), url: linkInput.trim(), type: "link" };
    onUpdate({ ...task, attachments: [...(task.attachments || []), attachment], updatedAt: "2026-03-24" });
    setLinkInput("");
  }

  return (
    <DialogPrimitives.Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogPrimitives.DialogContent className="max-w-[740px] !p-0 max-h-[88vh] flex flex-col overflow-hidden">
        <div className="td-modal-head">
          <div className="td-head-main">
            <div className="td-head-chips">
              <StatusChipComponent status={task.status} />
              <PriorityChipComponent priority={task.priority} />
              <DepartmentBadgeComponent dept={task.department} />
              <CustomerPriorityTagComponent cp={task.customerPriority} />
              <CategoryChip cat={task.taskCategory} />
            </div>
            <DialogPrimitives.DialogTitle className="td-modal-title">{task.title}</DialogPrimitives.DialogTitle>
            <div className="td-head-project">{project?.projectName} · {project?.clientName}</div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="td-section">
            <div className="td-section-label">Change Status</div>
            <div className="td-status-btns">
              {TASK_STATUSES.map((status) => <button key={status} className={`td-status-btn${task.status === status ? " active" : ""}`} onClick={() => changeStatus(status)}>{status}</button>)}
            </div>
          </div>
          <div className="td-section">
            <div className="td-section-label">Contributors</div>
            <div className="td-contrib-row">
              {allContribs.map((name) => <div key={name} className="td-contrib-pill"><AvatarComponent initials={getInit(name)} size={20} />{name}{name === task.assignee && <span className="td-contrib-owner">owner</span>}{name !== task.assignee && <span className="td-contrib-remove" onClick={() => removeContributor(name)}>×</span>}</div>)}
              {available.length > 0 && <select className="fsel td-contrib-select" value={contribSel} onChange={(event) => { addContributor(event.target.value); setContribSel(""); }}><option value="">+ Add contributor</option>{available.map((user) => <option key={user}>{user}</option>)}</select>}
            </div>
          </div>
          <div className="td-section">
            <div className="td-section-label">Task Details</div>
            <div className="td-details-grid">
              {[["Due Date", task.dueDate], ["Start Date", task.startDate || "—"], ["Department", task.department], ["Urgency", task.urgencyScore]].map(([key, value]) => <div key={key} className="td-detail-card"><div className="td-detail-label">{key}</div><div className="td-detail-value">{value}</div></div>)}
            </div>
            {task.notes && <div className="td-detail-notes">{task.notes}</div>}
          </div>
          <div className="td-section">
            <div className="td-section-label">Comments ({(task.taskComments || []).length})</div>
            <div className="td-comment-thread">
              {(task.taskComments || []).map((comment) => (
                <div key={comment.id} className="td-comment">
                  <AvatarComponent initials={comment.initials} size={26} />
                  <div className="td-comment-wrap">
                    <div className="td-comment-header">
                      <span className="td-comment-author">{comment.author}</span>
                      <span className="td-comment-meta">{relTime(comment.ts)}</span>
                      {(comment.mentions || []).map((mention) => <span key={mention} className="mention-badge" style={{ marginTop: 0 }}><AvatarComponent initials={getInit(mention)} size={12} />@{mention}</span>)}
                    </div>
                    <div className="td-comment-text"><RenderMentionsComponent text={comment.text} /></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="td-section">
              <MentionInputComponent value={commentText} onChange={setCommentText} placeholder="Write a comment… @mention to notify" rows={2} onSubmit={postComment} />
              <div className="td-comment-post-row"><button className="btn-add td-comment-post-btn" disabled={!commentText.trim()} onClick={postComment}>Post ⌘↵</button></div>
            </div>
          </div>
          <div className="td-section">
            <div className="td-section-label">Attachments &amp; Links</div>
            {(task.attachments || []).map((attachment) => <div key={attachment.id} className="td-attach-row"><span className="td-attach-icon">{attachment.type === "link" ? "🔗" : "📄"}</span><a href={attachment.url} target="_blank" rel="noreferrer" className="td-attach-name">{attachment.name}</a></div>)}
            <div className="td-attach-input-row"><input className="finput td-attach-input" placeholder="Paste a URL or file link…" value={linkInput} onChange={(event) => setLinkInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") addLink(); }} /><button className="btn-add td-attach-add-btn" onClick={addLink}>Add</button></div>
          </div>
          <div className="td-section">
            <div className="td-section-label">Activity Log</div>
            <div className="td-changelog">
              {[...(task.changelog || [])].reverse().map((entry) => <div key={entry.id} className="td-cl-row"><AvatarComponent initials={getInit(entry.author)} size={18} /><span className="td-cl-text"><strong>{entry.author}</strong> {entry.action || "changed"} <span className="td-cl-field">{entry.field}</span>{entry.from && <><span className="td-cl-arrow">from</span><span className="td-cl-from">{entry.from}</span></>} <span className="td-cl-arrow">→</span><span className="td-cl-val">{entry.to}</span></span><span className="td-cl-ts">{relTime(entry.ts)}</span></div>)}
            </div>
          </div>
        </div>
      </DialogPrimitives.DialogContent>
    </DialogPrimitives.Dialog>
  );
}

function UrgencyScore({ score, dueLabel, dueColor }) {
  const color = score >= 70 ? C.red : score >= 40 ? C.yellow : C.green;
  return <div className="tc-score-col"><div className="tc-score-bar" style={{ background: color }} /><span className="tc-score-num" style={{ color }}>{score}</span><span className="tc-due-lbl" style={{ color: dueColor || color }}>{dueLabel}</span></div>;
}

function AvatarStack({ names, AvatarComponent, size = 22 }) {
  if (!names || !names.length) return null;
  return <div className="avt-stack">{names.slice(0, 4).map((name, index) => <AvatarComponent key={index} initials={getInit(name)} size={size} />)}</div>;
}

function CategoryChip({ cat }) {
  const style = TASK_CAT_STYLE[cat];
  if (!style) return null;
  return <span className="cat-chip" style={{ color: style.fg, background: style.bg, border: `1px solid ${style.fg}22` }}>{cat}</span>;
}
